import { Theme } from '@monorepo/client-types';
import { ColorScheme } from '../color-scheme';
import { designTokens } from '../design-tokens';

const tokens = designTokens.colors;

const affiliateStatusPalette = {
  active: {
    bg: tokens.green[400],
    dot: tokens.green[500],
    bgDark: tokens.base.black,
    dotDark: tokens.green[400],
    borderDark: tokens.green[400],
  },
  pending: {
    bg: tokens.yellow[400],
    dot: tokens.yellow[600],
    bgDark: tokens.base.black,
    dotDark: tokens.yellow[400],
    borderDark: tokens.yellow[400],
  },
  archived: {
    bg: tokens.neutral[500],
    dot: tokens.neutral[700],
    bgDark: tokens.base.black,
    dotDark: tokens.neutral[500],
    borderDark: tokens.neutral[500],
  },
};

export const ttpColorSchemes: Record<Theme, ColorScheme> = {
  light: {
    palette: {
      primary: {
        main: tokens.pink[500],
      },
      secondary: {
        main: tokens.turquoise[300],
      },
      secondaryInvert: {
        main: tokens.grape[950],
      },
      warning: {
        main: tokens.orange[600],
      },
      info: {
        main: tokens.violet[400],
      },

      background: {
        default: tokens.base.white,
        paper: tokens.neutral[50],
        contrast: tokens.neutral[1300],
        opposite: tokens.neutral[1500],
        blue: tokens.blue[1000],
        button: tokens.violet[100],
        gradient: tokens.base.white, // Fallback for light theme
        hero: 'linear-gradient(251deg, #D97170 22.08%, #AA5887 63.39%, #7B4097 93.69%)',
      },

      sidebar: {
        collapsed: tokens.base.white,
        contentExpanded: tokens.base.white,
        iconsExpanded: tokens.neutral[300], // #EEEFEF
        border: tokens.neutral[300],
        iconColor: tokens.neutral[500], // #BBBDBF
        iconActive: tokens.pink[500], // Primary color for TTP
        iconActiveText: tokens.base.white,
        accentBg: tokens.violet[100], // #EAE2FF
        accentBgHover: tokens.grape[200], // #D5C9FC
        accentIcon: tokens.pink[500],
      },

      blue: {
        main: tokens.blue[550],
      },
      purple: {
        main: tokens.purple[1000],
      },
      success: {
        main: tokens.green[500],
        accent: tokens.green[1200],
      },
      yellow: {
        main: tokens.yellow[500],
      },
      orange: {
        main: tokens.orange[550],
      },
      error: {
        main: tokens.red[500],
      },

      text: {
        main: tokens.neutral[950],
        second: tokens.neutral[800],
        third: tokens.neutral[600],
        fourth: tokens.neutral[500],
        fifth: tokens.neutral[300],
        opposite: tokens.base.white,
        disabled: tokens.neutral[600],
        success: tokens.green[950],
        error: tokens.red[950],
        black: tokens.base.black,
      },

      pieChart: {
        main: tokens.purple[500],
        second: tokens.purple[1200],
        third: tokens.yellow[1000],
        fourth: tokens.green[1000],
      },
      admin: {
        primarySoft: tokens.violet[1000],
        blueSoft: tokens.blue[1200],
        greenSoft: tokens.green[1050],
        green: tokens.green[1000],
        primary: tokens.purple[500],
      },

      progressCard: {
        accent: tokens.pink[500], // #E959AC - TTP pink
        text: tokens.pink[500], // Pink text for TTP (status text) - light mode
        circleStroke: tokens.pink[500], // Pink progress circle
      },

      playbook: {
        main: tokens.blue[1300],
        second: tokens.purple[1300],
        third: tokens.green[1100],
        fourth: tokens.yellow[1100],
        fifth: tokens.orange[1000],
        sixth: tokens.red[1000],
        seventh: tokens.pink[1000],
      },

      affiliateStatus: affiliateStatusPalette,

      white: tokens.base.white,
    },
  },
  dark: {
    palette: {
      primary: {
        main: tokens.turquoise[300],
      },
      secondary: {
        main: tokens.pink[500],
      },
      secondaryInvert: {
        main: tokens.pink[500],
      },
      warning: {
        main: tokens.orange[600],
      },
      info: {
        main: tokens.violet[400],
      },

      background: {
        default: tokens.neutral[1500],
        paper: tokens.neutral[950],
        contrast: tokens.neutral[1400],
        opposite: tokens.base.white,
        button: tokens.purple[900],
        blue: tokens.blue[1100],
        gradient: `radial-gradient(25% 50% at 75% 2%, #65344E 0%, #2F1749 53.83%, #000000 100%)`,
        hero: 'linear-gradient(251deg, #D97170 22.08%, #AA5887 63.39%, #7B4097 93.69%)',
      },

      sidebar: {
        collapsed: tokens.neutral[1000], // #000000
        contentExpanded: tokens.neutral[1000], // #000000
        iconsExpanded: tokens.neutral[945], // #3E4145
        border: tokens.neutral[945], // #3E4145
        iconColor: tokens.neutral[500], // #BBBDBF
        iconActive: tokens.turquoise[300], // Primary color for TTP dark
        iconActiveText: tokens.neutral[950], // #06060C
        accentBg: tokens.grape[950], // #3e2359
        accentBgHover: tokens.grape[900], // #56317e
        accentIcon: tokens.base.white,
      },

      blue: {
        main: tokens.blue[400],
      },
      purple: {
        main: tokens.purple[1000],
      },
      success: {
        main: tokens.green[500],
        accent: tokens.green[1200],
      },
      yellow: {
        main: tokens.yellow[500],
      },
      orange: {
        main: tokens.orange[500],
      },
      error: {
        main: tokens.red[600],
      },

      text: {
        main: tokens.base.white,
        second: tokens.neutral[500],
        third: tokens.neutral[700],
        fourth: tokens.neutral[800],
        fifth: tokens.neutral[945],
        opposite: tokens.neutral[950],
        disabled: tokens.neutral[945],
        success: tokens.green[500],
        error: tokens.red[600],
        black: tokens.base.black,
      },

      admin: {
        primarySoft: tokens.violet[1000],
        blueSoft: tokens.blue[1200],
        greenSoft: tokens.green[1050],
        green: tokens.green[1000],
        primary: tokens.purple[500],
      },

      pieChart: {
        main: tokens.purple[500],
        second: tokens.purple[1200],
        third: tokens.yellow[1000],
        fourth: tokens.green[1000],
      },

      progressCard: {
        accent: tokens.turquoise[300], // Turquoise for TTP (button fill) - dark mode
        text: tokens.turquoise[300], // Turquoise text for TTP (status text) - dark mode
        circleStroke: tokens.turquoise[300], // Turquoise progress circle - dark mode
      },

      playbook: {
        main: tokens.blue[1300],
        second: tokens.purple[1300],
        third: tokens.green[1100],
        fourth: tokens.yellow[1100],
        fifth: tokens.orange[1000],
        sixth: tokens.red[1000],
        seventh: tokens.pink[1000],
      },

      affiliateStatus: affiliateStatusPalette,

      white: tokens.base.white,
    },
  },
};
